import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, ParamMap } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Actions, Select, Store, ofActionDispatched } from '@ngxs/store';
import {
  BillingAddressService,
  ConfirmStashedBillingAddress,
  ProductState,
  StashBillingAddressUpdateForConfirmation,
  SubscriptionBillingState
} from '@platform-frontend/checkout';
import { BillingAddress, BusinessModel, CountryState, CreateBillingAddress, UpdateBillingAddress } from '@platform-frontend/marketplace';
import { Navigate, SimpleRouterState } from '@platform-frontend/util';
import { Observable, combineLatest, of } from 'rxjs';
import { map, switchMap, take, takeUntil } from 'rxjs/operators';

@UntilDestroy()
@Component({
  selector: 'yat-billing-address',
  templateUrl: './billing-address.component.html',
  styleUrls: ['./billing-address.component.scss']
})
export class BillingAddressComponent implements OnInit {
  constructor(private actions$: Actions, private activatedRoute: ActivatedRoute, private billingAddressService: BillingAddressService, private store: Store) {}

  @Select(SimpleRouterState.queryParamMap)
  queryParams$: Observable<ParamMap>;

  @Select(SubscriptionBillingState.billingAddresses)
  billingAddresses$: Observable<BillingAddress[]>;

  @Select(CountryState.countries)
  countries$: Observable<string[]>;

  @Select(SubscriptionBillingState.selectedBillingAddress)
  selectedBillingAddress$: Observable<BillingAddress | null>;

  @Select(SubscriptionBillingState.selectedCountry)
  selectedCountry$: Observable<string | null>;

  @Select(SubscriptionBillingState.stashRequiresConfirmation)
  stashRequiresConfirmation$: Observable<boolean>;

  @Select(ProductState.businessModel)
  businessModel$: Observable<BusinessModel | null>;

  editedBillingAddress$: Observable<Partial<BillingAddress>>;

  ngOnInit(): void {
    this.editedBillingAddress$ = this.queryParams$.pipe(
      map(params => params.get('id')),
      map(stringId => (stringId === null ? null : parseInt(stringId, 10))),
      switchMap(editedId => this.billingAddressService.getEditableBillingAddress(editedId, this.selectedCountry$)),
      takeUntil(this.actions$.pipe(ofActionDispatched(CreateBillingAddress, UpdateBillingAddress, StashBillingAddressUpdateForConfirmation)))
    );
  }

  cancel(): void {
    this.billingAddresses$
      .pipe(
        take(1),
        map(billingAddresses => (billingAddresses.length > 0 ? '../billing-address-selection' : '../overview')),
        switchMap(url => this.store.dispatch(new Navigate([url], { relativeTo: this.activatedRoute }))),
        untilDestroyed(this)
      )
      .subscribe();
  }

  save(billingAddress: BillingAddress): void {
    const action: StashBillingAddressUpdateForConfirmation | UpdateBillingAddress | CreateBillingAddress = this.buildAction(billingAddress);
    this.store
      .dispatch(action)
      .pipe(
        switchMap(() => this.handleSaveActionSuccess(action instanceof CreateBillingAddress)),
        untilDestroyed(this)
      )
      .subscribe();
  }

  private buildAction(billingAddress: BillingAddress): StashBillingAddressUpdateForConfirmation | UpdateBillingAddress | CreateBillingAddress {
    const connectedAddress: BillingAddress | null = this.store.selectSnapshot(SubscriptionBillingState.connectedBillingAddress);
    if (connectedAddress !== null && connectedAddress.id === billingAddress.id) {
      return new StashBillingAddressUpdateForConfirmation(billingAddress);
    } else if (billingAddress.id !== 0) {
      return new UpdateBillingAddress(billingAddress);
    } else {
      return new CreateBillingAddress(billingAddress);
    }
  }

  private handleSaveActionSuccess(replaceUrl: boolean): Observable<unknown> {
    return this.userConfirmationRequired().pipe(
      switchMap(userConfirmationRequired =>
        userConfirmationRequired
          ? this.store.dispatch(new Navigate(['../update-confirmation'], { replaceUrl, relativeTo: this.activatedRoute }))
          : this.handleSaveActionSuccessWithoutUserConfirmation(replaceUrl)
      )
    );
  }

  private userConfirmationRequired(): Observable<boolean> {
    return combineLatest([this.editedBillingAddress$, this.selectedBillingAddress$, this.stashRequiresConfirmation$]).pipe(
      take(1),
      map(([edited, selected, confirmationRequired]) => (edited.id === undefined || edited.id === selected?.id) && confirmationRequired)
    );
  }

  private handleSaveActionSuccessWithoutUserConfirmation(replaceUrl: boolean): Observable<unknown> {
    const stashRequiresStorage = this.store.selectSnapshot(SubscriptionBillingState.stashRequiresStorage);
    return (stashRequiresStorage ? this.store.dispatch(new ConfirmStashedBillingAddress()) : of(null)).pipe(
      switchMap(() =>
        this.store.dispatch(
          new Navigate(['../billing-address-selection'], {
            replaceUrl,
            relativeTo: this.activatedRoute,
            clearNotifications: stashRequiresStorage ? false : undefined
          })
        )
      )
    );
  }
}
